import React from 'react'
import {observer} from 'mobx-react-lite'
import {discardMessageBlock, modalFooter, modalFooterButton, titleUnderline} from './styles.css'
import {Button, Flex, Stack, useDisclosure, Text, Box} from '../../../../vanilla'
import {Modal} from '../../../../components/modal'
import {CloseIcon, ExclamationCircleIcon} from '../../../../components/icons'
import {useBasketStore} from '../../../../store/hooks/useStore'
import {DateCalendar} from '../../../../components/date-calendar'
import { DeliveryTimeAndDate } from '../../../../components/book-delivery/delivery-time-and-date'

interface ChangingAddressWarningModalProps extends ReturnType<typeof useDisclosure> {
  onSubmit: () => void
}

export const ChangingAddressWarningModal = observer(function ChangingAddressWarningModal(
  props: ChangingAddressWarningModalProps
) {
  const {onClose, onSubmit} = props

  const {basket} = useBasketStore()

  const deliveryDate = basket?.c_windowEndTime ? new Date(basket.c_windowEndTime) : null

  return (
    <Modal size="full-lg" closeIcon={false} {...props}>
      <Stack width="full" spacing="0px">
        <Flex justify="flex-end" width="full" paddingX="16px" paddingY={['8px', '8px', '12px']}>
          <Button variant="unstyled" onClick={() => props.onClose()}>
            <CloseIcon boxSize="28px" color="gray800" />
          </Button>
        </Flex>
        <Box>
          <Text
            variant="heading3"
            lineHeight="shorter"
            marginBottom={['12px', '12px', '16px']}
            display="block"
            as="span"
            align="center"
          >
            Changing Your Address
          </Text>
          <Box className={titleUnderline} />
        </Box>

        <Stack spacing="20px">
          <Flex
            align="stretch"
            border="1px"
            borderColor="promotionColorDark"
            borderRadius="base"
            className={discardMessageBlock}
          >
            <Flex
              align="center"
              width="52px"
              bg="promotionColorDark"
              flexShrink="0"
              height="auto"
              justify="center"
            >
              <ExclamationCircleIcon color="white" boxSize="20px" />
            </Flex>
            <Box padding="12px">
              <Text variant="text3" lineHeight="shorter">
                If you change your address, you will need to pick a new delivery slot. Changing
                address can also impact the price and availability of some items.
              </Text>
            </Box>
          </Flex>
          <Flex
            maxWidth={['auto', 'two-thirds']}
            marginX={['20px', 'auto']}
            bg="gray50"
            padding="20px"
            gap="16px"
            marginBottom="32px"
          >
            {deliveryDate && <DateCalendar date={deliveryDate} variant="accent" size="md" />}
            <Stack spacing="16px" align="flex-start">
              <Text color="gray800" variant="text3" lineHeight="short">
                Your delivery time:
              </Text>
              <Flex gap="4px" align="flex-start" flexDirection='column' >
                <DeliveryTimeAndDate 
                  textProps={{variant: 'text3'}}
                  showDate={true}
                  dateFormat='EEEE do MMMM'
                  />
              </Flex>
            </Stack>
          </Flex>
        </Stack>

        <Flex
          gap="20px"
          align="center"
          justify={['flex-start', 'center']}
          direction={['column-reverse', 'row']}
          paddingX={['12px', '28px']}
          paddingTop="12px"
          paddingBottom="28px"
          className={modalFooter}
        >
          <Button
            width="full"
            size="sm"
            variant="outlineDark"
            onClick={() => onClose()}
            extraClassName={modalFooterButton}
          >
            Cancel Address Change
          </Button>
          <Button
            width="full"
            size="sm"
            variant="primary"
            onClick={() => {
              onSubmit()
              onClose()
            }}
            extraClassName={modalFooterButton}
          >
            Change My Address
          </Button>
        </Flex>
      </Stack>
    </Modal>
  )
})
